<template>
  <div>
    <div class="p-schedule-visit__content__title">
      <span class="heading-4">{{ title }}</span>
    </div>
    <div class="p-schedule-visit__content__visit-reason">
      <template v-if="userIsPractitioner">
        <span class="heading-5 p-schedule-visit__content__visit-reason__title">
          {{ $t("practitioner.schedule.choosePatient") }}
          <span class="p-schedule-visit__content__required">*</span>
        </span>
        <Select
          :label="$t('practitioner.schedule.choosePatientFromList')"
          :value="patientId"
          :hideDetails="false"
          :items="patientsOptions"
          @change="onPatientChange"
        />
      </template>
      <span class="heading-5 p-schedule-visit__content__visit-reason__title">
        {{ $t("visits.chiefComplaint") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Select
        :label="chiefComplaintLabel"
        :value="chiefComplaint"
        :hideDetails="false"
        :items="chiefComplaints"
        @change="onChiefComplaintChange"
      />
      <span class="heading-5 p-schedule-visit__content__visit-reason__title">
        {{ $t("visits.schedule.first.describeProblem") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Textarea
        :value="complaintDescription"
        :placeholder="$t('visits.schedule.first.visitReason')"
        :height="67"
        :hideDetails="true"
        @change="onVisitReasonChange"
      />
    </div>
    <div class="p-schedule-visit__content__calendar">
      <span class="heading-5 p-schedule-visit__content__calendar__title">
        <img class="p-schedule-visit__content__icon" src="../../../assets/icons/calendar-blue.svg" />
        {{ $t("visits.schedule.first.chooseDate") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <v-date-picker
        :allowed-dates="allowedDates"
        :value="visitDate"
        :max="maxScheduleDate"
        :min="minScheduleDate"
        reactive
        no-title
        :disabled="mode === 'edit'"
        outlined
        full-width
        class="p-schedule-visit__content__calendar__picker"
        @change="onVisitDateChange"
        color="primary"
      />
    </div>
    <div class="p-schedule-visit__content__time">
      <span class="heading-5 p-schedule-visit__content__time__title mb-2">
        <img class="p-schedule-visit__content__icon" src="../../../assets/icons/time-blue.svg" />
        {{ $t("visits.schedule.first.chooseTimeSlot") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Select
        :label="selectPlaceholder"
        :value="slotId"
        :hideDetails="true"
        :items="selectOptions"
        @change="onSelectChange"
        :disabled="mode === 'edit'"
      />
    </div>
    <div class="p-schedule-visit__content__btn-container">
      <PrimaryButton
        className="primary-btn__blue"
        :fullWidth="true"
        :text="submitButtonText"
        size="large"
        :disabled="isButtonDisabled"
        @onClick="onSubmit"
        :loading="buttonLoading"
      />
    </div>
  </div>
</template>
<script>
import { DateTime } from "luxon";
import Vue from "vue";
import { mapGetters, mapMutations } from "vuex";

import PrimaryButton from "@/components/uikit/PrimaryButton";
import Select from "@/components/uikit/Select";
import Textarea from "@/components/uikit/Textarea";
import { RolesEnum } from "@/types/Roles.enum";

export default Vue.extend({
  name: "CheckoutFirstStep",
  props: {
    mode: String,
    buttonLoading: { default: false, type: Boolean },
  },
  components: {
    Textarea,
    PrimaryButton,
    Select,
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapGetters("slots", ["slotsArray", "currentSlot"]),
    ...mapGetters("checkout", ["slotId", "complaintDescription", "chiefComplaint", "visitDate", "patientId"]),
    ...mapGetters("patients", ["practitionerOwnPatients"]),
    isButtonDisabled() {
      return !this.slotId || !this.complaintDescription || !this.chiefComplaint || !this.visitDate;
    },
    slotsDates() {
      return this.slotsArray.map((slot) => DateTime.fromISO(slot.start).toJSDate());
    },
    availableDates() {
      return [...new Set(this.slotsArray.map((slot) => DateTime.fromISO(slot.start).day))];
    },
    maxScheduleDate() {
      const dateTimes = this.slotsDates.map((date) => DateTime.fromJSDate(date));
      const date = dateTimes.length ? DateTime.max(...dateTimes).toJSDate() : new Date();
      return date.toISOString().substr(0, 10);
    },
    minScheduleDate() {
      const dateTimes = this.slotsDates.map((date) => DateTime.fromJSDate(date));
      const date = dateTimes.length ? DateTime.min(...dateTimes).toJSDate() : new Date();
      return date.toISOString().substr(0, 10);
    },
    selectOptions() {
      return this.slotsArray
        .filter((slot) => {
          const startTime = DateTime.fromISO(slot.start).toFormat("yyyy-MM-dd");
          return startTime === this.visitDate;
        })
        .map((slot) => {
          const startTime = DateTime.fromISO(slot.start).toFormat("t");
          const endTime = DateTime.fromISO(slot.end).toFormat("t");
          return {
            text: `${startTime} - ${endTime}`,
            value: slot.id,
          };
        });
    },
    selectPlaceholder() {
      if (this.mode === "edit" && this.currentSlot) {
        const startTime = DateTime.fromISO(this.currentSlot.start).toFormat("t");
        const endTime = DateTime.fromISO(this.currentSlot.end).toFormat("t");
        return `${startTime} - ${endTime}`;
      }
      if (this.visitDate) {
        return `${this.$t("visits.schedule.first.availableTimeFor")} ${DateTime.fromFormat(
          this.visitDate,
          "yyyy-MM-d",
        ).toFormat("LLLL dd")}`;
      }
      return `${this.$t("visits.schedule.first.availableTimeFor")} ${DateTime.local().toFormat("LLLL dd")}`;
    },
    chiefComplaints() {
      const chiefComplaints = this.$t("general.chiefComplaints");
      if (!chiefComplaints) {
        return [];
      }
      return Object.keys(chiefComplaints).map((key) => ({ text: chiefComplaints[key], value: key }));
    },
    patientsOptions() {
      return this.practitionerOwnPatients.map(({ firstName, lastName, id }) => ({
        text: `${firstName} ${lastName}`,
        value: id,
      }));
    },
    chiefComplaintLabel() {
      const ops = {
        [RolesEnum.Patient]: this.$t("visits.schedule.first.chooseYourComplaint"),
        [RolesEnum.Practitioner]: this.$t("practitioner.schedule.choosePatientComplaint"),
      };
      return ops[this.role] || "";
    },
    title() {
      const ops = {
        [RolesEnum.Patient]: this.$t("visits.schedule.first.title"),
        [RolesEnum.Practitioner]: this.$t("practitioner.schedule.title"),
      };
      return ops[this.role] || "";
    },
    submitButtonText() {
      const ops = {
        [RolesEnum.Patient]: this.$t("visits.schedule.first.title"),
        [RolesEnum.Practitioner]: this.$t("practitioner.schedule.title"),
      };
      return ops[this.role] || "";
    },
    userIsPractitioner() {
      return this.role === RolesEnum.Practitioner;
    },
  },
  methods: {
    ...mapMutations("checkout", ["setStringFieldByName", "setVisitDate"]),
    allowedDates(val) {
      return this.availableDates.includes(DateTime.fromISO(val).day);
    },
    onSelectChange(value) {
      this.setStringFieldByName({ value, fieldName: "slotId" });
    },
    onChiefComplaintChange(value) {
      this.setStringFieldByName({ value, fieldName: "chiefComplaint" });
    },
    onVisitReasonChange(value) {
      this.setStringFieldByName({ value, fieldName: "complaintDescription" });
    },
    onVisitDateChange(value) {
      this.setVisitDate(value);
    },
    onSubmit() {
      this.$emit("submit", 2);
    },
    onPatientChange(value) {
      this.setStringFieldByName({ value, fieldName: "patientId" });
    },
  },
});
</script>
